import { useParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import HeroSection from '../home/components/HeroSection';
import TrustBar from '../home/components/TrustBar';
import CategoryGrid from '../home/components/CategoryGrid';
import AIBuyingGuide from '../home/components/AIBuyingGuide';
import FeaturedProducts from '../home/components/FeaturedProducts';
import WhyChooseUs from '../home/components/WhyChooseUs';
import ShippingBenefits from '../home/components/ShippingBenefits';
import Testimonials from '../home/components/Testimonials';
import FAQSection from '../home/components/FAQSection';
import FinalCTA from '../home/components/FinalCTA';
import { featuredParts } from '../../mocks/products';

type CategoryConfig = {
  slug: string;
  heroTag: string;
  heroTitle1: string;
  heroTitleHighlight: string;
  heroTitle2: string;
  heroSubtitle: string;
  heroBg: string;
  categoryTitle: string;
  categorySubtitle: string;
  aiMainQ: string;
  aiMainA: string;
  productsTitle: string;
  productsSubtitle: string;
  faqTitle: string;
  faqItems: Array<{ q: string; a: string }>;
};

const categoryConfigs: Record<string, CategoryConfig> = {
  'vertical-blind-parts': {
    slug: 'vertical-blind-parts',
    heroTag: 'Over 200 Vertical Blind Parts In Stock — Ships in 1–2 Days',
    heroTitle1: 'Vertical Blind',
    heroTitleHighlight: 'Replacement Parts',
    heroTitle2: 'Carriers, Stems, Vanes & More',
    heroSubtitle:
      'Find every replacement component for vertical blinds — carrier stems, vane weights, bottom chains, headrail clips, and full slat replacements. Fast nationwide shipping.',
    heroBg:
      'https://public.readdy.ai/ai/img_res/94609810c54c54f573db009b850b2179.jpg',
    categoryTitle: 'Shop Vertical Blind Parts',
    categorySubtitle: 'Browse all replacement components for vertical blind systems by type.',
    aiMainQ: 'What Vertical Blind Parts Do I Need?',
    aiMainA: 'The most common vertical blind repairs involve replacing broken carrier stems, missing vane weights, cracked slats, or worn bottom chains. Carrier stems are the plastic hooks that hold each slat — they break frequently. Vane weights keep slats hanging straight. Bottom chains connect slats so they rotate together. All are standard-fit and easy to replace.',
    productsTitle: 'Vertical Blind Parts',
    productsSubtitle: 'Universal-fit parts for most vertical blind brands. Ships in 1–2 business days.',
    faqTitle: 'Vertical Blind Parts FAQ',
    faqItems: [
      {
        q: 'What part holds vertical blind slats in place?',
        a: 'The carrier stem (also called a vane stem or hook) holds each slat in the track. It snaps into the carrier body and the slat clips onto it. When a slat falls, the carrier stem is usually the broken part. They are sold individually and are simple to replace in under 5 minutes per slat.',
      },
      {
        q: 'How do I replace a vertical blind vane?',
        a: 'Vertical blind vane replacement takes about 2 minutes per slat. Open the carrier stem, remove the old vane, and slide the new one in. Make sure the vane size matches your existing slats — measure length and width. Our 82-inch standard slats fit the majority of residential vertical blind systems.',
      },
      {
        q: 'Why do my vertical blinds not rotate together?',
        a: 'If vertical blind slats don\'t rotate uniformly, the bottom chain connecting them may be broken or missing. The bottom chain links slats so the tilt rod movement rotates them all at once. Replacing the chain takes about 10 minutes and is an inexpensive fix — typically costing only a few dollars per set.',
      },
      {
        q: 'What are vertical blind vane weights for?',
        a: 'Vane weights are small plastic inserts that sit at the bottom of each vertical blind slat. Their job is to keep slats hanging straight and prevent them from blowing around when a window is open. When missing, slats flap and look messy. They snap in easily and cost very little to replace.',
      },
    ],
  },
  'horizontal-blind-parts': {
    slug: 'horizontal-blind-parts',
    heroTag: 'Over 180 Horizontal Blind Parts In Stock — Nationwide Shipping',
    heroTitle1: 'Horizontal Blind',
    heroTitleHighlight: 'Replacement Parts',
    heroTitle2: 'Headrails, End Caps, Cord Locks & More',
    heroSubtitle:
      'Complete selection of horizontal blind components — headrails, end caps, cord locks, ladder tape, tilt mechanisms, and slat replacements for all major brands.',
    heroBg:
      'https://readdy.ai/api/search-image?query=close%20up%20flat%20lay%20product%20photography%20of%20horizontal%20blind%20parts%20headrail%20components%20end%20caps%20cord%20locks%20and%20slats%20organized%20on%20white%20background%20professional%20e-commerce%20hardware%20photography%20sharp%20clean%20neutral%20tones%20industrial&width=1920&height=1080&seq=hero_hbparts_v3&orientation=landscape',
    categoryTitle: 'Shop Horizontal Blind Parts',
    categorySubtitle: 'All replacement components for horizontal blinds — 1-inch and 2-inch systems.',
    aiMainQ: 'What Horizontal Blind Parts Do I Need?',
    aiMainA: 'Horizontal blind repairs most commonly require replacing the cord lock (which keeps the blind at a set height), the headrail end caps (which cap the ends of the headrail), or the tilt mechanism (which rotates the slats). Individual slats can also be replaced. Most parts are universal-fit across major 1-inch and 2-inch horizontal blind systems.',
    productsTitle: 'Horizontal Blind Parts',
    productsSubtitle: 'Fix your horizontal blinds with our universal-fit replacement components.',
    faqTitle: 'Horizontal Blind Parts FAQ',
    faqItems: [
      {
        q: 'What is a horizontal blind cord lock?',
        a: 'A cord lock is the mechanism at the top of a horizontal blind that holds the lift cord in place at a set height. When the cord lock wears out, the blind slowly drops on its own. Replacement is simple — snap in a new cord lock and re-thread the cord. Most cord locks are universal-fit for 1-inch and 2-inch headrails.',
      },
      {
        q: 'Can I replace just the end caps on a horizontal blind?',
        a: 'Yes — end caps are sold individually or as pairs and are simple to replace. They snap onto the ends of the headrail. When end caps crack or break, the internal components can fall out. Match the headrail width when ordering. Most standard residential headrails use 1-inch or 2-inch end caps available in our catalog.',
      },
      {
        q: 'How do I fix a horizontal blind that won\'t tilt?',
        a: 'If the slats won\'t tilt when you turn the wand, the tilt mechanism inside the headrail is likely worn or broken. The tilt tube rotates via a small gear or clutch mechanism. Replacing the tilt mechanism takes about 15 minutes. Alternatively, a broken or disconnected tilt wand hook can also cause this — check both before ordering.',
      },
      {
        q: 'What part do I need if my horizontal blind fell off the wall?',
        a: 'If the blind fell from the window, the mounting bracket has likely broken or come loose. Mounting brackets are sold in pairs for inside or outside mount. Measure the headrail width to match bracket size — typically 1-inch or 1.5-inch for standard residential blinds. Replacement brackets install with two small screws and take under 10 minutes.',
      },
    ],
  },
  'mini-blind-parts': {
    slug: 'mini-blind-parts',
    heroTag: 'Over 350 Mini Blind Parts — Cords, Wands, Ladders & More',
    heroTitle1: 'Mini Blind',
    heroTitleHighlight: 'Replacement Parts',
    heroTitle2: 'Cords, Wands, Ladders & Hardware',
    heroSubtitle:
      'Complete selection of mini blind repair parts — lift cords, ladder tapes, tilt wands, cord locks, slat replacements, and more. All universal-fit and ready to ship.',
    heroBg:
      'https://readdy.ai/api/search-image?query=close%20up%20flat%20lay%20product%20photography%20of%20mini%20blind%20replacement%20parts%20including%20lift%20cords%20wands%20ladders%20cord%20locks%20on%20white%20background%20professional%20e-commerce%20hardware%20photography%20sharp%20detail%20warm%20neutral%20tones&width=1920&height=1080&seq=hero_mbparts_v3&orientation=landscape',
    categoryTitle: 'Shop Mini Blind Parts',
    categorySubtitle: 'Every component you need to repair horizontal mini blinds.',
    aiMainQ: 'What Mini Blind Parts Do I Need?',
    aiMainA: 'Mini blind repairs most commonly involve replacing the lift cord (which allows the blind to raise and lower), the tilt wand (which rotates the slats), the cord lock mechanism, or individual slats. Lift cords break from daily use and are the most frequently replaced part. Replacement is simple and costs only a few dollars per blind.',
    productsTitle: 'Mini Blind Parts & Hardware',
    productsSubtitle: 'Fix your mini blinds in minutes with our universal-fit replacement parts.',
    faqTitle: 'Mini Blind Repair FAQ',
    faqItems: [
      {
        q: 'How do I replace a mini blind lift cord?',
        a: 'Remove the blind from the window and lay it flat. Pull out the bottom rail plugs and remove the broken cord. Thread the new cord up through all slat holes, through the cord lock at the top, and back down the other side. Tie a secure knot at the bottom, replace the tassel, and reinstall. Takes about 20 minutes total.',
      },
      {
        q: 'What size lift cord do I need for my mini blinds?',
        a: 'Most mini blinds use a standard 0.9mm or 1.0mm diameter braided lift cord. Measure the height of your window and double it, adding 24 inches for knotting room. We sell lift cord in 9-foot, 18-foot, and 30-foot lengths to cover most window sizes. Our 18-foot cord handles windows up to 8 feet tall.',
      },
      {
        q: 'My mini blind tilt wand broke — what do I need?',
        a: 'Tilt wands attach to the tilt tube via a hook connector. Most wands are universal-fit — just match the length and hook style. Standard wands come in 14, 18, 24, and 28-inch lengths. The hook end snaps onto the wand hook at the headrail. Clear acrylic wands are the most popular replacement option.',
      },
      {
        q: 'Can I replace individual slats on mini blinds?',
        a: 'Yes — individual slat replacement is easy and inexpensive. You need to match the width (1-inch or 2-inch) and color of your existing slats. Open the bottom rail, remove the old slat from the ladder tape, and slide the new one in. Match color and finish as closely as possible for a seamless repair.',
      },
    ],
  },
  'valance-clips': {
    slug: 'valance-clips',
    heroTag: '60+ Valance Clip Types In Stock — Fast Shipping Nationwide',
    heroTitle1: 'Valance Clips',
    heroTitleHighlight: '& Valance Hardware',
    heroTitle2: 'Snap Clips, Return Clips & Hold-Down Pins',
    heroSubtitle:
      'Full selection of valance clips for horizontal blinds and vertical blinds — snap-on clips, return side clips, hold-down pins, and valance attach brackets. Universal-fit options available.',
    heroBg:
      'https://readdy.ai/api/search-image?query=macro%20flat%20lay%20product%20photography%20of%20assorted%20plastic%20window%20blind%20valance%20clips%20snap%20clips%20and%20return%20hardware%20organized%20neatly%20on%20white%20clean%20background%20e-commerce%20product%20shot%20sharp%20isolated%20industrial%20parts%20photography%20minimal&width=1920&height=1080&seq=hero_valclips_v3&orientation=landscape',
    categoryTitle: 'Shop Valance Clips',
    categorySubtitle: 'Replacement clips and hardware to keep your blind valance securely in place.',
    aiMainQ: 'What Valance Clips Do I Need?',
    aiMainA: 'Valance clips are small plastic clips that snap onto the front of a blind headrail and hold the decorative valance panel in place. When clips break, the valance droops or falls off. The most common types are snap-front clips (which clip onto the top front of the headrail) and return side clips (which hold the valance ends flush to the wall). Match the headrail style when ordering.',
    productsTitle: 'Valance Clips & Hardware',
    productsSubtitle: 'Universal-fit valance clips compatible with most major blind brands.',
    faqTitle: 'Valance Clips FAQ',
    faqItems: [
      {
        q: 'What are valance clips and what do they do?',
        a: 'Valance clips are small plastic mounting clips that attach a decorative valance to the front of a blind headrail. They snap onto the headrail lip and hold the valance panel securely in place. When valance clips crack or break, the valance droops or falls off. Most are sold in packs of 4 and are inexpensive to replace.',
      },
      {
        q: 'How do I know which valance clip fits my blinds?',
        a: 'Valance clips are sized to match the headrail of your blind. Most standard 1-inch and 2-inch horizontal blinds use universal snap-front clips. If your valance clips onto the top of the headrail, you need top-mount clips. If it wraps over the front, you need front-snap clips. Bring your old clip to compare, or call our parts team for help.',
      },
      {
        q: 'What are return clips for valances?',
        a: 'Return clips are side clips that hold the ends of a valance flush to the wall on the left and right sides of the window. Without return clips, the valance ends flare outward. They are sold as a pair and are easy to install. Match the valance thickness when ordering — standard return clips fit most residential blind valances.',
      },
      {
        q: 'Can I replace just one broken valance clip?',
        a: 'Yes — valance clips are sold individually and in multi-packs. If only one or two clips are broken, you can replace just those. We recommend replacing all clips at the same time if the blind is older, as the remaining clips may be brittle. A pack of 4 clips typically costs under $6 and ships same or next business day.',
      },
    ],
  },
  'mounting-brackets': {
    slug: 'mounting-brackets',
    heroTag: '120+ Mounting Bracket Types In Stock — Inside, Outside & Ceiling',
    heroTitle1: 'Blind Mounting',
    heroTitleHighlight: 'Brackets & Hardware',
    heroTitle2: 'Inside Mount, Outside Mount & Ceiling',
    heroSubtitle:
      'Universal mounting brackets, valance clips, end caps, and headrail hardware for all blind and shade types. Secure installation starts with the right bracket.',
    heroBg:
      'https://readdy.ai/api/search-image?query=organized%20flat%20lay%20of%20assorted%20metal%20and%20plastic%20window%20blind%20mounting%20brackets%20inside%20and%20outside%20mount%20types%20clean%20white%20background%20professional%20e-commerce%20product%20photography%20sharp%20clean%20detail%20isolated%20industrial%20hardware&width=1920&height=1080&seq=hero_mbrack_v3&orientation=landscape',
    categoryTitle: 'Shop Mounting Brackets',
    categorySubtitle: 'Replacement mounting brackets for secure blind and shade installation.',
    aiMainQ: 'What Mounting Bracket Do I Need?',
    aiMainA: 'Mounting brackets come in two main types: inside mount (installs inside the window frame for a built-in look) and outside mount (installs above or beside the frame). Choose based on your window depth and preference. Bracket size must match your headrail width — most standard residential blinds use 1-inch or 1.5-inch brackets. Both metal and plastic brackets are available.',
    productsTitle: 'Mounting Brackets',
    productsSubtitle: 'Universal-fit mounting brackets for horizontal blinds, vertical blinds, and roller shades.',
    faqTitle: 'Mounting Brackets FAQ',
    faqItems: [
      {
        q: 'What is the difference between inside and outside mount brackets?',
        a: 'Inside mount brackets attach inside the window frame, allowing the blind to sit within the frame for a clean, built-in look. Outside mount brackets attach above or outside the frame, ideal when the frame is too shallow for inside mounting. Both types are available in our catalog and are standard fit for most blind headrails.',
      },
      {
        q: 'How do I know which mounting bracket fits my blind?',
        a: 'Brackets are sized to fit the headrail width of your blind — typically 1-inch, 1.5-inch, or 2-inch wide. Measure your existing headrail or old bracket width. Most standard residential blinds use 1.5-inch brackets. If unsure, call our parts team — we can help identify the correct bracket from your blind description.',
      },
      {
        q: 'Can I replace just one broken mounting bracket?',
        a: 'Yes — brackets are sold individually and in pairs. If one bracket is broken, you can replace just that one as long as you match the style and width. We recommend replacing in pairs when possible to ensure even weight distribution. Brackets typically cost between $4–$12 per pair and ship within 1–2 business days.',
      },
      {
        q: 'What is a ceiling mount bracket used for?',
        a: 'Ceiling mount brackets allow a blind or shade to be installed on the ceiling instead of the wall — useful for skylights, angled ceilings, or inside-mount applications where the wall angle makes standard mounting difficult. Ceiling mount brackets are also used for room-darkening applications where no light gaps are desired around the window.',
      },
    ],
  },
  'chains-cords': {
    slug: 'chains-cords',
    heroTag: '120+ Chain, Cord & Control Hardware Items In Stock',
    heroTitle1: 'Chains, Cords',
    heroTitleHighlight: '& Control Hardware',
    heroTitle2: 'For Shades, Blinds & Drapery',
    heroSubtitle:
      'Replacement bead chains, pull cords, child safety devices, cord tensioners, and wall brackets for roller shades, cellular shades, and drapery systems.',
    heroBg:
      'https://readdy.ai/api/search-image?query=flat%20lay%20close%20up%20product%20photography%20of%20window%20shade%20bead%20chains%20pull%20cords%20safety%20brackets%20and%20cord%20accessories%20on%20white%20background%20clean%20e-commerce%20product%20photo%20sharp%20isolated%20warm%20tones%20industrial%20hardware%20parts&width=1920&height=1080&seq=hero_chains_v3&orientation=landscape',
    categoryTitle: 'Shop Chains & Cords',
    categorySubtitle: 'Control hardware, safety devices, and cord accessories for all window covering types.',
    aiMainQ: 'What Chain or Cord Part Do I Need?',
    aiMainA: 'Roller shades and cellular shades use a bead chain loop to raise, lower, and tilt the shade. When the chain breaks or the loop connector fails, the shade becomes inoperable. Bead chains are measured by bead diameter (most are 4.5mm) and total loop length. Safety cord cleats and tensioners are also available to meet child safety requirements.',
    productsTitle: 'Chain & Cord Parts',
    productsSubtitle: 'Universal-fit chains, cords, and control accessories. Ships in 1–2 business days.',
    faqTitle: 'Chain & Cord Parts FAQ',
    faqItems: [
      {
        q: 'What size bead chain do I need for my roller shade?',
        a: 'Most modern roller shades use 4.5mm bead chain. Older systems may use 3.5mm or 6mm beads. Measure the diameter of an existing bead if possible, or count the beads per inch — 4.5mm chains have approximately 5.5 beads per inch. Chain loop length depends on shade height — add 20 inches to your shade height for comfortable operation.',
      },
      {
        q: 'How do I replace a broken bead chain loop on a roller shade?',
        a: 'The bead chain connects via a small plastic connector at the loop joint. Open the connector, remove the broken chain, and thread the new chain through the control mechanism. Reconnect with the connector. The entire process takes under 10 minutes and requires no tools. Our chains come pre-cut with a connector included.',
      },
      {
        q: 'What are cord safety cleats and do I need them?',
        a: 'Cord safety cleats are wall-mounted hooks that allow you to wrap and secure a dangling blind cord out of a child\'s reach. They are a low-cost safety solution required by safety guidelines in homes with young children. Simply screw the cleat into the wall beside the window and wrap the cord around it when not in use.',
      },
      {
        q: 'My shade won\'t stay in position — what part do I need?',
        a: 'If a roller shade rolls up or down on its own, the internal spring tension needs adjustment — this is a shade mechanism issue, not a cord issue. If a cordless cellular shade won\'t hold position, the cord tensioner inside the bottom rail may be worn. Contact our parts team to help diagnose whether you need a new spring mechanism or cord tensioner.',
      },
    ],
  },
  'drapery-hardware': {
    slug: 'drapery-hardware',
    heroTag: '150+ Drapery Hardware Parts — Rods, Carriers & Rings',
    heroTitle1: 'Drapery & Curtain',
    heroTitleHighlight: 'Hardware Parts',
    heroTitle2: 'Traverse Rods, Rings & Carriers',
    heroSubtitle:
      'Full selection of drapery hardware components — traverse rod carriers, curtain rings, hooks, cord pulleys, and rod end caps. Compatible with standard traverse rod systems.',
    heroBg:
      'https://readdy.ai/api/search-image?query=flat%20lay%20of%20drapery%20hardware%20components%20curtain%20rings%20hooks%20and%20traverse%20rod%20carriers%20on%20white%20background%20professional%20e-commerce%20product%20photography%20clean%20isolated%20sharp%20detail%20neutral%20warm%20tones%20hardware%20parts&width=1920&height=1080&seq=hero_draphw_v3&orientation=landscape',
    categoryTitle: 'Shop Drapery Hardware',
    categorySubtitle: 'Replacement components for traverse rods, curtain tracks, and drapery systems.',
    aiMainQ: 'What Drapery Hardware Parts Do I Need?',
    aiMainA: 'Drapery hardware repair most often involves replacing traverse rod carriers (the slides that hold curtain hooks), replacing the master carrier for two-way draw systems, or replacing worn cord or pulleys. Curtain rings and hooks are also frequently replaced. Most components are universal-fit across standard traverse rod systems.',
    productsTitle: 'Drapery Hardware Parts',
    productsSubtitle: 'Compatible with most standard traverse rod systems. Ships in 1–2 business days.',
    faqTitle: 'Drapery Hardware FAQ',
    faqItems: [
      {
        q: 'What is a traverse rod carrier?',
        a: 'A traverse rod carrier is the small slide that moves along the traverse rod track and holds the curtain hook. When you pull the draw cord, the carriers slide together to close the curtain and apart to open it. Carriers wear out over time and can be replaced individually. Most are universal fit for standard two-way draw traverse rods.',
      },
      {
        q: 'My traverse rod curtains won\'t close properly — what is the fix?',
        a: 'The most common cause is a broken or stuck master carrier, which is the center carrier that drives the entire system. It may be cracked, jammed, or disconnected from the cord. Replacing the master carrier is typically a $5–$10 fix. We carry universal master carriers compatible with most standard two-way draw traverse rod systems.',
      },
      {
        q: 'What curtain rings fit standard curtain rods?',
        a: 'Curtain ring size depends on the rod diameter. Standard curtain rods range from 3/4-inch to 1.5-inch diameter. Match the ring inner diameter to your rod size. We carry rings in plastic, metal, and decorative finishes. Clip rings (with a clip instead of a hook) allow fabric to hang from the ring directly without sewing.',
      },
      {
        q: 'Can I find parts for older traverse rod systems?',
        a: 'Yes — many traverse rod components are industry-standard and compatible across systems made over the past 30+ years. Call our parts specialists with your rod brand and description. We frequently help customers find replacement parts for discontinued Graber, Kirsch, and Conso traverse rod systems.',
      },
    ],
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const config = (slug && categoryConfigs[slug]) || categoryConfigs['vertical-blind-parts'];
  const categoryProducts = featuredParts.filter((p) => p.categorySlug === config.slug);
  const displayProducts = categoryProducts.length >= 2 ? categoryProducts : featuredParts.slice(0, 4);

  const breadcrumbItems = [
    { label: 'Shop Parts', href: '/category/vertical-blind-parts' },
    { label: config.categoryTitle.replace('Shop ', '') },
  ];

  return (
    <main className="min-h-screen font-sans">
      <Navbar />
      <HeroSection
        content={{
          tag: config.heroTag,
          titleLine1: config.heroTitle1,
          titleHighlight: config.heroTitleHighlight,
          titleLine2: config.heroTitle2,
          subtitle: config.heroSubtitle,
          ctaPrimary: 'Shop Parts Now',
          ctaPrimaryHref: '#products',
          ctaSecondary: 'Find Your Part',
          ctaSecondaryHref: '#categories',
          bgImage: config.heroBg,
          stats: [
            { value: '1,000+', label: 'Total Parts' },
            { value: 'Since 1997', label: 'In Business' },
            { value: '1–2 Days', label: 'Ships In' },
            { value: '4.9★', label: 'Rating' },
          ],
        }}
      />
      <Breadcrumb items={breadcrumbItems} />
      <TrustBar />
      <CategoryGrid title={config.categoryTitle} subtitle={config.categorySubtitle} />
      <AIBuyingGuide content={{ mainQ: config.aiMainQ, mainA: config.aiMainA }} />
      <FeaturedProducts
        title={config.productsTitle}
        subtitle={config.productsSubtitle}
        products={displayProducts}
      />
      <WhyChooseUs />
      <ShippingBenefits />
      <Testimonials />
      <FAQSection content={{ title: config.faqTitle, items: config.faqItems }} />
      <FinalCTA />
      <Footer />
    </main>
  );
}
